import { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Card, Input, message, Modal, Progress, Spin } from 'antd';
import ProxiedImage from 'components/proxied-image';
import { Copy } from 'lucide-react';
import authenticationApi from 'apis/authentication';
import i18n from 'i18n';
import LOCALS from 'commons/locals';
import formatTime from 'utils/formatTime';
import { getVerdictStyle, getPointScoreColor } from './authentication-config';
import {
  type AuthenticationDisplayDetectionPoint,
  type AuthenticationDisplaySummary,
  getAuthenticationDisplaySummary,
  getAuthenticationDisplayScoreText,
  getAuthenticationDisplayVerdictText,
} from './authentication-display';

const SCORE_ANIMATION_DURATION = 900;
const SCORE_BAR_COLOR = '#1677ff';
const SUMMARY_PROGRESS_TRAIL_COLOR = '#e5e7eb';

const clampScore = (score: number) => Math.max(0, Math.min(100, score));

// displaySummary 只会产出 mode: 'normal' | 'few-sample-positive'，
// 'normal' 下 displayVerdictKind 只会是 'genuine' | 'suspected-fake'
export const getAuthenticationDetailVerdictStyle = (
  displayVerdictKind: AuthenticationDisplaySummary['displayVerdictKind'],
  score: number | undefined | null,
) => {
  if (displayVerdictKind === 'genuine') {
    return { backgroundColor: '#2E7D32', color: '#FFFFFF' };
  }
  if (displayVerdictKind === 'suspected-fake') {
    return { backgroundColor: '#C62828', color: '#FFFFFF' };
  }
  return getVerdictStyle(score);
};

type BagMeta = {
  brand?: string;
  name?: string;
  names?: string;
  color?: string;
  rank?: string;
  material?: string;
  hardware?: string;
  stamp?: string;
  accessory?: string;
  size?: string;
};

type ImageItem = {
  id: number;
  publicUrl: string;
  backendPath?: string;
  originalName: string;
};

type LatestResult = {
  status: 'processing' | 'completed' | 'failed' | 'queued';
  authenticityScore: number;
  authenticityLevel: string;
  confidence: number;
  verdict: string;
  reviewStatus?: 'pending' | 'genuine' | 'fake' | null;
};

type DetectionPoint = {
  id: number;
  pointId: string;
  category: string;
  labelId: number;
  coordinates: { x: number; y: number; width: number; height: number };
  score: number;
  similarity: number;
  status: string;
  description: string | null;
  referenceImages: string[];
  originalName: string | null;
  publicUrl: string | null;
  reviewedBy: string | null;
  reviewedAt: string | null;
  reviewComment: string | null;
};

type AuthenticationDetailData = {
  bagInfo: {
    id: number;
    bagId: string;
    bagMeta: BagMeta;
  };
  images: ImageItem[];
  latestTask: {
    authenticationId: string;
    status: 'processing' | 'completed' | 'failed' | 'queued';
    certificateNo?: string;
    bagInfo?: BagMeta;
  } | null;
  latestResult: LatestResult | null;
  detectionPoints: DetectionPoint[];
};

type AuthenticationDetailModalProps = {
  open: boolean;
  authenticationBagId: number | undefined;
  localPreviewByOriginalName?: Record<string, string>;
  onClose: () => void;
};

const AuthenticationDetailModal = ({
  open,
  authenticationBagId,
  localPreviewByOriginalName = {},
  onClose,
}: AuthenticationDetailModalProps) => {
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState<AuthenticationDetailData | null>(null);
  const [animatedPointScores, setAnimatedPointScores] = useState<
    Record<number, number>
  >({});
  const [reviewComments, setReviewComments] = useState<Record<number, string>>(
    {},
  );
  const [batchSubmitting, setBatchSubmitting] = useState(false);
  const submittedReviewCommentsRef = useRef<Record<number, string>>({});

  const fetchDetail = useCallback(async () => {
    if (!authenticationBagId) return;
    setLoading(true);
    setReviewComments({});
    try {
      const result = await authenticationApi.getAuthenticationDetail(
        String(authenticationBagId),
      );
      if (result) {
        setDetail(result);
        const seededComments = Object.fromEntries(
          (result.detectionPoints || [])
            .filter((point) => !!resolvePointImageUrl(point))
            .map((point) => [point.id, point.reviewComment || '']),
        );
        setReviewComments(seededComments);
        submittedReviewCommentsRef.current = seededComments;
      }
    } catch (error) {
      message.error(String(i18n.t(LOCALS.authentication_detail_fetch_failed)));
    } finally {
      setLoading(false);
    }
  }, [authenticationBagId]);

  useEffect(() => {
    if (open && authenticationBagId) {
      fetchDetail();
    }
  }, [open, authenticationBagId, fetchDetail]);

  useEffect(() => {
    if (!open || loading || !detail?.detectionPoints?.length) {
      setAnimatedPointScores({});
      return;
    }

    const targetScores = detail.detectionPoints.reduce<Record<number, number>>(
      (acc, point) => {
        acc[point.id] = clampScore(point.score);
        return acc;
      },
      {},
    );

    setAnimatedPointScores(
      detail.detectionPoints.reduce<Record<number, number>>((acc, point) => {
        acc[point.id] = 0;
        return acc;
      }, {}),
    );

    const startAt = performance.now();
    let frameId = 0;

    const step = (now: number) => {
      const progress = Math.min(1, (now - startAt) / SCORE_ANIMATION_DURATION);
      const nextScores = Object.entries(targetScores).reduce<
        Record<number, number>
      >((acc, [id, target]) => {
        acc[Number(id)] = target * progress;
        return acc;
      }, {});
      setAnimatedPointScores(nextScores);

      if (progress < 1) {
        frameId = requestAnimationFrame(step);
      }
    };

    frameId = requestAnimationFrame(step);

    return () => {
      if (frameId) {
        cancelAnimationFrame(frameId);
      }
    };
  }, [open, loading, detail]);

  const handleCopyId = () => {
    const no =
      detail?.latestTask?.certificateNo || detail?.latestTask?.authenticationId;
    if (no) {
      navigator.clipboard.writeText(no);
      message.success(String(i18n.t(LOCALS.authentication_no_copied)));
    }
  };

  const resolvePointImageUrl = (point: AuthenticationDisplayDetectionPoint) => {
    return (
      point.publicUrl ||
      (point.originalName
        ? localPreviewByOriginalName[point.originalName] || ''
        : '') ||
      point.referenceImages?.[0] ||
      ''
    );
  };

  const validDetectionPoints = (detail?.detectionPoints || []).filter(
    (point) => !!resolvePointImageUrl(point),
  );

  const handleReviewCommentChange = (pointId: number, value: string) => {
    setReviewComments((prev) => ({ ...prev, [pointId]: value }));
  };

  const handleSubmitAllReviewComments = async () => {
    const entries = Object.entries(reviewComments)
      .map(([id, comment]) => ({
        id: Number(id),
        reviewComment: comment.trim(),
      }))
      .filter(
        (entry) =>
          entry.reviewComment &&
          entry.reviewComment !==
            (submittedReviewCommentsRef.current[entry.id] || '').trim(),
      );

    if (!entries.length) {
      message.warning(
        String(i18n.t(LOCALS.authentication_point_review_nothing_to_submit)),
      );
      return;
    }

    setBatchSubmitting(true);
    try {
      const results = await Promise.allSettled(
        entries.map((entry) =>
          authenticationApi.updateDetectionPointReview(entry),
        ),
      );

      const succeededIds = new Set(
        results
          .map((result, index) =>
            result.status === 'fulfilled' ? entries[index].id : null,
          )
          .filter((id): id is number => id !== null),
      );
      const failedCount = results.length - succeededIds.size;
      const submittedCommentById = new Map(
        entries.map((entry) => [entry.id, entry.reviewComment]),
      );

      if (succeededIds.size) {
        let refreshedPoints: DetectionPoint[] | null = null;
        if (authenticationBagId) {
          try {
            const refreshed = await authenticationApi.getAuthenticationDetail(
              String(authenticationBagId),
            );
            refreshedPoints = refreshed?.detectionPoints || null;
          } catch (error) {
            // reviewedBy/reviewedAt 展示会退化为提交前的值，不影响本次提交结果
          }
        }

        setDetail((prev) =>
          prev
            ? {
                ...prev,
                detectionPoints: prev.detectionPoints.map((point) => {
                  if (!succeededIds.has(point.id)) return point;
                  const refreshedPoint = refreshedPoints?.find(
                    (p) => p.id === point.id,
                  );
                  return {
                    ...point,
                    reviewComment:
                      refreshedPoint?.reviewComment ??
                      submittedCommentById.get(point.id) ??
                      point.reviewComment,
                    reviewedBy: refreshedPoint?.reviewedBy ?? point.reviewedBy,
                    reviewedAt: refreshedPoint?.reviewedAt ?? point.reviewedAt,
                  };
                }),
              }
            : prev,
        );
        succeededIds.forEach((id) => {
          submittedReviewCommentsRef.current[id] =
            submittedCommentById.get(id) ?? '';
        });
      }

      if (failedCount > 0) {
        message.warning(
          String(
            i18n.t(LOCALS.authentication_point_review_batch_result, {
              success: succeededIds.size,
              failed: failedCount,
            }),
          ),
        );
      } else {
        message.success(
          String(i18n.t(LOCALS.authentication_point_review_submit_success)),
        );
      }
    } finally {
      setBatchSubmitting(false);
    }
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className="flex justify-center items-center h-64">
          <Spin size="large" />
        </div>
      );
    }

    if (!detail) {
      return (
        <div className="text-center text-gray-500 py-8">
          {i18n.t(LOCALS.authentication_detail_not_found)}
        </div>
      );
    }

    const { bagInfo, images, latestTask, latestResult, detectionPoints } =
      detail;
    const rawBagMeta: unknown = bagInfo?.bagMeta || latestTask?.bagInfo;
    const bagMeta: BagMeta =
      typeof rawBagMeta === 'string'
        ? JSON.parse(rawBagMeta)
        : (rawBagMeta as BagMeta) || {};

    const resolveImageUrl = (image?: ImageItem) => {
      if (!image) return '';
      return (
        image.publicUrl ||
        image.backendPath ||
        localPreviewByOriginalName[image.originalName] ||
        ''
      );
    };

    const displaySummary = getAuthenticationDisplaySummary({
      latestResult,
      detectionPoints,
    });
    const verdictText = getAuthenticationDisplayVerdictText(
      displaySummary,
      i18n.t.bind(i18n),
    );
    const scoreText = getAuthenticationDisplayScoreText(
      displaySummary,
      i18n.t.bind(i18n),
    );

    const firstImageUrl = resolveImageUrl(images?.[0]);

    return (
      <div>
        {/* 标题和编号 */}
        <div className="mb-4">
          <h2 className="text-xl font-bold mb-2">
            {bagMeta.name || i18n.t(LOCALS.authentication_report_title)}
          </h2>
          <div className="flex items-center gap-2 text-gray-500 text-sm">
            <span>
              {i18n.t(LOCALS.authentication_no)}：
              {latestTask?.certificateNo || latestTask?.authenticationId || '-'}
            </span>
            <Button
              type="text"
              size="small"
              icon={<Copy className="w-3 h-3" />}
              onClick={handleCopyId}
            >
              {i18n.t(LOCALS.copy)}
            </Button>
          </div>
        </div>

        {/* 产品信息卡片 */}
        <Card className="mb-4" size="small">
          <div className="flex gap-6">
            {/* 产品图片 */}
            <div className="w-32 h-32 bg-gray-100 rounded-lg flex items-center justify-center overflow-hidden flex-shrink-0">
              {firstImageUrl ? (
                <ProxiedImage
                  src={firstImageUrl}
                  alt={String(i18n.t(LOCALS.authentication_product_image))}
                  className="w-full h-full object-cover"
                />
              ) : (
                <span className="text-gray-400 text-sm">
                  {i18n.t(LOCALS.authentication_no_image)}
                </span>
              )}
            </div>

            {/* 产品信息 */}
            <div className="flex-1">
              {/* 鉴定结论 */}
              <div className="flex flex-wrap items-center gap-x-4 gap-y-3 mb-4">
                <span className="text-gray-600 text-sm font-medium">
                  {i18n.t(LOCALS.authentication_conclusion)}：
                </span>
                {displaySummary.mode === 'few-sample-positive' ? (
                  <>
                    <span className="font-medium text-sm px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-700">
                      {verdictText}
                    </span>
                    <span className="px-2 py-0.5 rounded-full text-xs bg-yellow-100 text-yellow-700">
                      {scoreText}
                    </span>
                  </>
                ) : (
                  <span
                    className="font-medium text-sm px-2 py-0.5 rounded-full"
                    style={getAuthenticationDetailVerdictStyle(
                      displaySummary.displayVerdictKind,
                      displaySummary.displayScore,
                    )}
                  >
                    {verdictText}
                  </span>
                )}
              </div>

              {/* 产品属性 */}
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="flex">
                  <span className="text-gray-500 flex-shrink-0 whitespace-nowrap mr-2">
                    {i18n.t(LOCALS.name)}：
                  </span>
                  <span>{bagMeta.name || '-'}</span>
                </div>
                <div className="flex">
                  <span className="text-gray-500 flex-shrink-0 whitespace-nowrap mr-2">
                    {i18n.t(LOCALS.material)}：
                  </span>
                  <span>{bagMeta.material || '-'}</span>
                </div>
                <div className="flex">
                  <span className="text-gray-500 flex-shrink-0 whitespace-nowrap mr-2">
                    {i18n.t(LOCALS.measurement)}：
                  </span>
                  <span>{bagMeta.size || '-'}</span>
                </div>
                <div className="flex">
                  <span className="text-gray-500 flex-shrink-0 whitespace-nowrap mr-2">
                    {i18n.t(LOCALS.rank)}：
                  </span>
                  <span>{bagMeta.rank || '-'}</span>
                </div>
                <div className="flex">
                  <span className="text-gray-500 flex-shrink-0 whitespace-nowrap mr-2">
                    {i18n.t(LOCALS.authentication_year)}：
                  </span>
                  <span>{bagMeta.stamp || '-'}</span>
                </div>
                <div className="flex">
                  <span className="text-gray-500 flex-shrink-0 whitespace-nowrap mr-2">
                    {i18n.t(LOCALS.color)}：
                  </span>
                  <span>{bagMeta.color || '-'}</span>
                </div>
                <div className="flex">
                  <span className="text-gray-500 flex-shrink-0 whitespace-nowrap mr-2">
                    {i18n.t(LOCALS.hardware)}：
                  </span>
                  <span>{bagMeta.hardware || '-'}</span>
                </div>
              </div>
            </div>
          </div>
        </Card>

        {/* 检测点列表 */}
        {validDetectionPoints.length > 0 && (
          <div className="grid grid-cols-3 gap-3">
            {validDetectionPoints.map((point) => {
              const animatedScore = Math.round(
                animatedPointScores[point.id] ?? 0,
              );
              return (
                <Card key={point.id} size="small" className="overflow-hidden">
                  {/* 检测点标题 */}
                  <div
                    className="font-medium text-sm leading-5 mb-2 break-words"
                    style={{ minHeight: '2.5rem' }}
                  >
                    {point.category}
                  </div>

                  {/* 检测点图片：validDetectionPoints 已按有图过滤，这里必定有图 */}
                  <div className="relative mb-2">
                    <ProxiedImage
                      src={resolvePointImageUrl(point)}
                      alt={String(point.category || '')}
                      className="w-full h-28 object-cover rounded"
                    />
                  </div>

                  {/* AI鉴定结果 */}
                  <div className="mb-2">
                    <span className="text-gray-500 text-xs">
                      {i18n.t(LOCALS.authentication_ai_result)}：
                    </span>
                    <span
                      className="text-xs ml-1"
                      style={{
                        color:
                          point.status === 'not_found'
                            ? '#9ca3af'
                            : getPointScoreColor(point.score),
                      }}
                    >
                      {point.description ||
                        (point.status === 'passed'
                          ? i18n.t(LOCALS.authentication_point_passed)
                          : point.status === 'failed'
                            ? i18n.t(LOCALS.authentication_point_failed)
                            : point.status === 'not_found'
                              ? i18n.t(LOCALS.authentication_point_not_found)
                              : point.status)}
                    </span>
                  </div>

                  {/* 分数进度条 */}
                  <div>
                    <Progress
                      percent={animatedScore}
                      strokeColor={SCORE_BAR_COLOR}
                      trailColor={SUMMARY_PROGRESS_TRAIL_COLOR}
                      showInfo={false}
                      size="small"
                    />
                    <div className="flex justify-between text-xs text-gray-400">
                      <span>{i18n.t(LOCALS.authentication_score_zero)}</span>
                      <span style={{ color: SCORE_BAR_COLOR }}>
                        {i18n.t(LOCALS.authentication_score_point, {
                          score: animatedScore,
                        })}
                      </span>
                      <span>{i18n.t(LOCALS.authentication_score_full)}</span>
                    </div>
                  </div>

                  {/* 人工复核意见 */}
                  <div className="mt-2">
                    {point.reviewedBy ? (
                      <div className="mb-1 text-xs text-gray-400 leading-tight">
                        <div>
                          {i18n.t(LOCALS.authentication_point_reviewed_by, {
                            reviewer: point.reviewedBy,
                          })}
                        </div>
                        <div>
                          {i18n.t(LOCALS.authentication_point_reviewed_at, {
                            time: formatTime(point.reviewedAt),
                          })}
                        </div>
                      </div>
                    ) : null}
                    <Input.TextArea
                      rows={2}
                      maxLength={500}
                      disabled={batchSubmitting}
                      value={reviewComments[point.id] || ''}
                      placeholder={String(
                        i18n.t(LOCALS.authentication_point_review_placeholder),
                      )}
                      onChange={(e) =>
                        handleReviewCommentChange(point.id, e.target.value)
                      }
                    />
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  return (
    <Modal
      title={String(i18n.t(LOCALS.authentication_detail_title))}
      open={open}
      onCancel={onClose}
      width={800}
      destroyOnClose
      styles={{ body: { maxHeight: '65vh', overflowY: 'auto' } }}
      footer={
        !loading && validDetectionPoints.length > 0 ? (
          <Button
            type="primary"
            loading={batchSubmitting}
            onClick={handleSubmitAllReviewComments}
          >
            {i18n.t(LOCALS.authentication_point_review_submit)}
          </Button>
        ) : null
      }
    >
      {renderContent()}
    </Modal>
  );
};

export default AuthenticationDetailModal;
